import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as THREE from "three";

import { useAppBootstrapOptional } from "@/context/AppBootstrapContext";
import { useKaiChatOptional } from "@/features/kai/KaiChatContext";
import { useTheme, type ResolvedTheme } from "@/context/ThemeContext";
import { WAVE_FRAG, WAVE_VERT } from "./waveShaders";

/** UV Y anchor (Three.js: 0 = bottom, 1 = top). Content pages tuck the band lower than Kai. */
const WAVE_BAND_SPLASH = 0.5;
const WAVE_BAND_KAI = 0.2;
/** Mobile Kai welcome — band sits higher to clear bottom nav + composer. */
const WAVE_BAND_KAI_MOBILE = 0.36;
const WAVE_BAND_CONTENT = -0.01;
/** Mobile content pages — slightly higher than desktop so the band clears bottom nav. */
const WAVE_BAND_CONTENT_MOBILE = 0.05;
const KAI_MOBILE_MQ = "(max-width: 767px)";
const BAND_LERP_TAU_S = 0.14;
/** Splash → page: longer ease so the band settles down instead of dropping. */
const BAND_LERP_TAU_SPLASH_S = 0.79;
const BAND_SETTLE_EPS = 0.002;
const CENTER_FLOOR_CONTENT_MULT = 0.25;
/** Scales final wave output on content pages — effects stay identical, just dimmer. */
const WAVE_BRIGHTNESS_KAI = 1.0;
const WAVE_BRIGHTNESS_CONTENT = 0.36;
/** 1 while the Twido logo is up; eases to 0 as splash exits. */
const SPLASH_BLOOM = 1.0;

interface ThemePalette {
  base: THREE.Color;
  wave: THREE.Color;
  waveViolet: THREE.Color;
  waveSunset: THREE.Color;
  centerFloor: number;
  /** Route brightness multiplier on content pages (Kai uses 1.0). */
  contentBrightness: number;
}

const PALETTES: Record<ResolvedTheme, ThemePalette> = {
  dark: {
    base: new THREE.Color("#0a1424"),
    wave: new THREE.Color("#4ab8e8"),
    waveViolet: new THREE.Color("#4ab8e8"),
    waveSunset: new THREE.Color("#4ab8e8"),
    centerFloor: 0.08,
    contentBrightness: WAVE_BRIGHTNESS_CONTENT,
  },
  light: {
    base: new THREE.Color("#e8edf5"),
    wave: new THREE.Color("#c484d4"),
    waveViolet: new THREE.Color("#7d62dc"),
    waveSunset: new THREE.Color("#f09868"),
    centerFloor: 0.1,
    contentBrightness: 0.42,
  },
};

/**
 * Clean WebGL backdrop — solid fill with a single horizontal wave band.
 */
export function WaveBackgroundThree() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const bootstrap = useAppBootstrapOptional();
  const kai = useKaiChatOptional();
  const onKaiWelcome = pathname === "/" && (kai?.isWelcome ?? true);
  const onKaiWelcomeRef = useRef(onKaiWelcome);
  const kaiMobileRef = useRef(
    typeof window !== "undefined"
      ? window.matchMedia(KAI_MOBILE_MQ).matches
      : false,
  );
  const bootPhaseRef = useRef(bootstrap?.phase ?? "ready");
  /** Set by the render effect; lets state changes restart a frozen RAF loop. */
  const wakeRef = useRef<() => void>(() => {});

  useEffect(() => {
    onKaiWelcomeRef.current = onKaiWelcome;
    wakeRef.current();
  }, [onKaiWelcome]);

  useEffect(() => {
    const mq = window.matchMedia(KAI_MOBILE_MQ);
    const sync = () => {
      kaiMobileRef.current = mq.matches;
      wakeRef.current();
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    bootPhaseRef.current = bootstrap?.phase ?? "ready";
    wakeRef.current();
  }, [bootstrap?.phase]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const palette = PALETTES[theme];
    const isLight = theme === "light" ? 1.0 : 0.0;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    container.appendChild(renderer.domElement);

    const camera = new THREE.OrthographicCamera(0, 1, 1, 0, -1, 1);
    camera.position.z = 0.5;

    const scene = new THREE.Scene();

    const uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uBase: { value: palette.base.clone() },
      uWave: { value: palette.wave.clone() },
      uWaveViolet: { value: palette.waveViolet.clone() },
      uWaveSunset: { value: palette.waveSunset.clone() },
      uBandCenter: {
        value: WAVE_BAND_SPLASH,
      },
      uCenterFloor: { value: palette.centerFloor },
      uContentDim: {
        value: WAVE_BRIGHTNESS_KAI,
      },
      uIsLight: { value: isLight },
      uSplashBloom: { value: SPLASH_BLOOM },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader: WAVE_VERT,
      fragmentShader: WAVE_FRAG,
      uniforms,
      depthWrite: false,
      depthTest: false,
    });

    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
    mesh.position.set(0.5, 0.5, 0);
    scene.add(mesh);

    function resize() {
      const w = container!.clientWidth;
      const h = container!.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      renderer.domElement.style.width = `${w}px`;
      renderer.domElement.style.height = `${h}px`;
      uniforms.uResolution.value.set(w, h);
      // Re-render in case the loop is idle/frozen on a settled content page.
      wakeRef.current();
    }

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    let rafId = 0;
    const start = performance.now();
    let lastFrame = start;
    let currentBandCenter = WAVE_BAND_SPLASH;
    let currentContentDim = WAVE_BRIGHTNESS_KAI;
    let currentCenterFloor = palette.centerFloor;
    let currentSplashBloom = SPLASH_BLOOM;
    let splashDescent = true;

    function resolveTargetBand() {
      const bootPhase = bootPhaseRef.current;
      if (bootPhase === "splash") return WAVE_BAND_SPLASH;
      if (onKaiWelcomeRef.current) {
        return kaiMobileRef.current ? WAVE_BAND_KAI_MOBILE : WAVE_BAND_KAI;
      }
      return kaiMobileRef.current ? WAVE_BAND_CONTENT_MOBILE : WAVE_BAND_CONTENT;
    }

    function resolveTargetDim() {
      const bootPhase = bootPhaseRef.current;
      if (bootPhase === "splash") return WAVE_BRIGHTNESS_KAI;
      return onKaiWelcomeRef.current
        ? WAVE_BRIGHTNESS_KAI
        : palette.contentBrightness;
    }

    function resolveTargetCenterFloor() {
      const bootPhase = bootPhaseRef.current;
      if (bootPhase === "splash" || onKaiWelcomeRef.current) {
        return palette.centerFloor;
      }
      return palette.centerFloor * CENTER_FLOOR_CONTENT_MULT;
    }

    function resolveTargetBloom() {
      return bootPhaseRef.current === "splash" ? SPLASH_BLOOM : 0;
    }

    const EPS = 0.001;

    function scheduleFrame() {
      if (rafId === 0) rafId = requestAnimationFrame(frame);
    }

    /** Restart the loop after it froze/paused (route, theme, or tab change). */
    function wake() {
      lastFrame = performance.now();
      scheduleFrame();
    }

    function frame(now: number) {
      rafId = 0;
      // Tab hidden: stop the loop entirely (no busy RAF). visibilitychange
      // resumes it — cheaper than scheduling a frame we won't render.
      if (document.hidden) return;

      const dt = Math.min((now - lastFrame) / 1000, 0.05);
      lastFrame = now;

      const targetBand = resolveTargetBand();
      const targetDim = resolveTargetDim();
      const targetCenterFloor = resolveTargetCenterFloor();
      const targetBloom = resolveTargetBloom();

      if (bootPhaseRef.current === "splash") {
        splashDescent = true;
      } else if (Math.abs(targetBand - currentBandCenter) < BAND_SETTLE_EPS) {
        splashDescent = false;
      }

      if (prefersReducedMotion) {
        currentBandCenter = targetBand;
        currentContentDim = targetDim;
        currentCenterFloor = targetCenterFloor;
        currentSplashBloom = targetBloom;
        splashDescent = false;
      } else {
        const tau = splashDescent ? BAND_LERP_TAU_SPLASH_S : BAND_LERP_TAU_S;
        const lerp = 1 - Math.exp(-dt / tau);
        currentBandCenter += (targetBand - currentBandCenter) * lerp;
        currentContentDim += (targetDim - currentContentDim) * lerp;
        currentCenterFloor += (targetCenterFloor - currentCenterFloor) * lerp;
        currentSplashBloom += (targetBloom - currentSplashBloom) * lerp;
      }

      uniforms.uBandCenter.value = currentBandCenter;
      uniforms.uContentDim.value = currentContentDim;
      uniforms.uCenterFloor.value = currentCenterFloor;
      uniforms.uSplashBloom.value = currentSplashBloom;
      uniforms.uTime.value = prefersReducedMotion ? 0 : (now - start) / 1000;
      renderer.render(scene, camera);

      // Wave motion (uTime) runs on every page — Kai and content. Band / dim /
      // bloom still lerp independently and settle; that Y-axis transition is
      // unchanged. Reduced motion freezes after those values settle.
      const settled =
        Math.abs(targetBand - currentBandCenter) < BAND_SETTLE_EPS &&
        Math.abs(targetDim - currentContentDim) < EPS &&
        Math.abs(targetCenterFloor - currentCenterFloor) < EPS &&
        Math.abs(targetBloom - currentSplashBloom) < EPS;
      const keepAnimating = !prefersReducedMotion || !settled;

      if (keepAnimating) scheduleFrame();
    }

    wakeRef.current = wake;

    const onVisibility = () => {
      if (!document.hidden) wake();
    };
    document.addEventListener("visibilitychange", onVisibility);

    scheduleFrame();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      wakeRef.current = () => {};
      if (rafId !== 0) cancelAnimationFrame(rafId);
      ro.disconnect();
      material.dispose();
      mesh.geometry.dispose();
      renderer.dispose();
      if (renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [theme]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[var(--app-vvh,var(--app-vh,100vh))] w-full"
    />
  );
}
